import { memo } from 'react';
import { domainCode } from '../../constants/staticPageConstants';
import { handleTrack } from '../../utils/tracker';
import { FooterStyle } from './styles';
import { TCommonComponent, TLink, TMainHead, TSubHead } from './types';

const MainHead = memo(function MainHead({
  heading,
  borderBottomRequired = true,
}: TMainHead): JSX.Element {
  return (
    <div className={`mainHead ${borderBottomRequired && 'borderBottom'}`}>
      {heading}
    </div>
  );
});

const SubHead = memo(function SubHead({ text }: TSubHead): JSX.Element {
  return <div className="subHead">{text}</div>;
});

const SectionHead = memo(function SectionHead({ text }: TSubHead): JSX.Element {
  return <div className="sectionHead">{text}</div>;
});

const FooterListItem = memo(function FooterListItem({
  text,
  link,
}: TLink): JSX.Element {
  return (
    <div className="my-2">
      <a
        href={link}
        className="list list-width"
        onClick={(e) => {
          handleTrack({ label: text, obj: e.target });
        }}
      >
        {text}
      </a>
    </div>
  );
});

const List = memo(function List({ text, link }: TLink): JSX.Element {
  return (
    <div className="mb-1">
      <a
        href={link}
        className="list"
        onClick={(e) => {
          handleTrack({ label: 'footer_cta', obj: e.target });
        }}
      >
        {text}
      </a>
    </div>
  );
});

const ButtonList = memo(function ButtonList({
  text,
  link,
}: TLink): JSX.Element {
  return (
    <a
      href={link}
      className="buttonList"
      onClick={(e) => {
        handleTrack({
          label: 'footer_cta',
          obj: e.target,
        });
      }}
    >
      <div>{text}</div>
    </a>
  );
});

const CommonFooterComponent = memo(function CommonFooterComponent({
  language,
  data,
}: TCommonComponent): JSX.Element {
  const reference_extension = language === 'Arabic' ? '_ar' : '';
  return (
    <FooterStyle data-testid="CommonFooter">
      <div
        className={
          'bg-black py-20 background-color' +
          (language === 'Arabic' ? ' rtl' : '')
        }
      >
        <div className="wg-container">
          <div className="flex gap-4 sm:flex-col">
            <div className=" w-1/4 sm:w-full md:w-2/3">
              <img
                src="https://assets.website-files.com/6141e3720a248d2024615d96/62fe088d215f0851a61a141d_logo-cleartax.svg"
                alt="Clear Logo"
                width="153"
                height="28"
                loading="lazy"
              />
              <div className="sm:flex md:gap-4 sm:gap-4">
                <div className="sm:w-1/2">
                  <div className="mt-10">
                    <SubHead
                      text={
                        data.FOLLOW_US[
                          `heading${reference_extension}`
                        ] as string
                      }
                    />
                    <div className="flex gap-2 sm:gap-1">
                      {data.FOLLOW_US.images.map((follow) => (
                        <a key={follow.imageLink} href={follow.redirectUrl}>
                          <img
                            src={follow.imageLink}
                            alt={follow.altText}
                            width="24"
                            height="24"
                            loading="lazy"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full flex sm:flex-col mb-10 justify-between">
              <div className="w-full flex sm:flex-col mb-10 justify-between testing">
                {data.FOOTER_COL_SETS.slice(
                  0,
                  data.FOOTER_COL_SETS.length - 2
                ).map((sec, index) => (
                  <div className="w-6/12 md:w-1/2 sm:w-1/2 " key={index}>
                    {sec.heading ? (
                      <SectionHead
                        text={sec[`heading${reference_extension}`] as string}
                      />
                    ) : null}
                    {sec.child.map((item) => {
                      return (
                        <div key={item.id}>
                          <SectionHead
                            text={
                              item[`subHead${reference_extension}`] as string
                            }
                          />
                          {item.lists.map((list) => (
                            <FooterListItem
                              key={`${item.id}_${list.text}`}
                              text={
                                list[`text${reference_extension}`] as string
                              }
                              link={
                                list[`link${reference_extension}`] as string
                              }
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              {/* next line */}
              <div className="w-7/12 ml-[100px] md:w-1/2 sm:w-1/2 mb-10 sm:ml-[1px] ">
                {data.FOOTER_COL_SETS.slice(
                  data.FOOTER_COL_SETS.length - 2
                ).map((sec, index) => {
                  return (
                    <div className="w-full md:w-1/2 sm:w-1/2 " key={index}>
                      {sec.heading ? (
                        <SectionHead
                          text={sec[`heading${reference_extension}`] as string}
                        />
                      ) : null}
                      {sec.child.map((item) => {
                        return (
                          <div key={item.id}>
                            {item.lists.map((list) => (
                              <FooterListItem
                                key={`${item.id}_${list.text}`}
                                text={
                                  list[`text${reference_extension}`] as string
                                }
                                link={
                                  list[`link${reference_extension}`] as string
                                }
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex sm:flex-col justify-between w-full">
            <div className="text-white pb-4">
              {data.LEGAL[`heading${reference_extension}`]}
              <br />
              <div className="mb-[-26px]">
                <a
                  target="blank"
                  href="https://www.cleartax.com/my/legal"
                  className="list"
                  style={{ marginRight: '20px', display: 'inline-block' }}
                >
                  {data.LEGAL[`legal${reference_extension}`]}
                </a>
                {domainCode.cleartaxSA ? (
                  <a
                    target="blank"
                    href="https://www.cleartax.com/sa/privacy"
                    className="list"
                  >
                    {data.LEGAL[`privacy${reference_extension}`]}
                  </a>
                ) : (
                  <a
                    target="blank"
                    href="https://www.cleartax.com/my/privacy"
                    className="list"
                  >
                    {data.LEGAL[`privacy${reference_extension}`]}
                  </a>
                )}
              </div>
              <br />
              {data.LEGAL[`terms${reference_extension}`]}
            </div>
            <div className="col-12 col-sm-6 border-left text-right">
              <img
                loading="lazy"
                alt="website-footer-log"
                src="https://assets.website-files.com/6141e3720a248d2024615d96/62fc9def97ed4eb67d9c62e9_Group%2017219.svg"
                width="150"
                height="30"
              />
            </div>
          </div>
        </div>
      </div>
    </FooterStyle>
  );
});
export {
  ButtonList,
  CommonFooterComponent,
  FooterListItem,
  List,
  MainHead,
  SectionHead,
  SubHead,
};
